import { type FC } from "react"

import { type Course } from "@forento/core/models/course"

import Dropdown from "~/components/Dropdown"
import { query } from "~/utilities/trpc"

import { Description, Title } from "./styles"

type Props = {
	courseId: number
	prerequisiteCourseId: number | null
	onChange(prerequisiteCourseId: number | null): void
}
const Prerequisite: FC<Props> = ({ courseId, prerequisiteCourseId, onChange }) => {
	const courses = query.course.list.useQuery()

	return (
		<>
			<Title>Prerequisite</Title>
			<Description>
				Require students to complete another course before they can start this one. Students can still buy this
				course in advance, and those who already started it keep their access.
			</Description>
			{courses.error ? (
				<p className="text-gray-500">Failed to load courses.</p>
			) : courses.data === undefined ? (
				<p className="text-gray-500">Loading courses...</p>
			) : (
				<>
					<Dropdown
						className="max-w-md"
						items={[
							{ id: "none", title: "No prerequisite" },
							...courses.data
								.filter(course => course.id !== courseId)
								.map(course => ({
									id: course.id.toString(),
									title:
										course.publishDate === null ? `${course.title} (not published)` : course.title,
									// A course that already requires this one, directly or through its own prerequisites,
									// would never unlock.
									isDisabled: dependsOn(courses.data, course.id, courseId),
								})),
						]}
						selectedItemId={prerequisiteCourseId?.toString() ?? "none"}
						onChange={value => onChange(value === "none" ? null : Number(value))}
					/>
					{prerequisiteCourseId !== null &&
						courses.data.find(course => course.id === prerequisiteCourseId)?.publishDate === null && (
							<p className="mt-2 text-sm text-gray-500">
								The prerequisite only applies while that course is published.
							</p>
						)}
				</>
			)}
		</>
	)
}

function dependsOn(courses: Course[], courseId: number, targetId: number): boolean {
	const visited = new Set<number>()
	let currentId: number | null = courseId
	while (currentId !== null && !visited.has(currentId)) {
		const course = courses.find(x => x.id === currentId)
		if (course?.prerequisiteCourseId === targetId) return true
		visited.add(currentId)
		currentId = course?.prerequisiteCourseId ?? null
	}
	return false
}

export default Prerequisite
